// Hideo - the app's own dialogs in place of the browser's alert / confirm / prompt, without UI: the defaults of each kind,
// what a dialog answers and what a key does. The open dialogs, the thumbs-up and the sync notice are in
// src/stores/notices.ts (uiConfirm, uiPrompt, uiAlert, uiSuccess, syncToast), drawn by src/components/notices/.
import { t } from '../i18n'

/** Everything uiDialog can be asked (each one optional) */
export interface UiDialogOptions {
  /** Plain text (shown as it is, line breaks kept): pass raw user data in t() placeholders, never esc() */
  message?: unknown
  /** Plain text in bold above the message; '' = none */
  title?: unknown
  /** Text of the main button */
  ok?: unknown
  /** Text of the cancel button; '' = no cancel button (an alert) */
  cancel?: unknown
  /** Starting text of a text field (a prompt); null = no field */
  input?: string | null
  /** Destructive action: the icon on a full brand background */
  danger?: boolean
  /** Font Awesome icon class(es) */
  icon?: string
}

/** A dialog ready to be shown: every field filled in */
export interface UiDialogSpec {
  message: string
  title: string
  ok: string
  cancel: string
  input: string | null
  danger: boolean
  icon: string
}

/** uiConfirm's options */
export interface ConfirmOptions {
  ok?: string
  cancel?: string
  danger?: boolean
  title?: string
  icon?: string
}
/** uiPrompt's options (its cancel button always says "Cancel") */
export interface PromptOptions {
  ok?: string
  title?: string
  icon?: string
}

const text = (v: unknown) => String(v ?? '')

/** The defaults of uiDialog: OK without cancel button, info icon, no field */
export function dialogSpec(o: UiDialogOptions = {}): UiDialogSpec {
  return {
    message: text(o.message),
    title: o.title ? text(o.title) : '',
    ok: o.ok === undefined ? 'OK' : text(o.ok),
    cancel: o.cancel ? text(o.cancel) : '',
    input: o.input === undefined || o.input === null ? null : text(o.input),
    danger: !!o.danger,
    icon: o.icon === undefined ? 'fa-circle-info' : String(o.icon)
  }
}

/** A question with OK / Cancel (a danger one with the bin icon) */
export const confirmSpec = (message: unknown, o: ConfirmOptions = {}) => dialogSpec({ message, ok: o.ok || t('OK'), cancel: o.cancel || t('Cancel'),
  danger: !!o.danger, title: o.title || '', icon: o.icon || (o.danger ? 'fa-trash' : 'fa-circle-question') })

/** A question with a text field */
export const promptSpec = (message: unknown, value: unknown = '', o: PromptOptions = {}) => dialogSpec({ message, input: text(value),
  ok: o.ok || t('OK'), cancel: t('Cancel'), title: o.title || '', icon: o.icon || 'fa-pen' })

/** A message with OK only */
export const alertSpec = (message: unknown) => dialogSpec({ message: text(message), ok: t('OK'), icon: 'fa-circle-info' })

/** What the dialog answers: with a text field the text (or null when cancelled), otherwise true / false */
export function dialogResult(spec: UiDialogSpec, yes: boolean, value: string): boolean | string | null {
  return spec.input !== null ? (yes ? value : null) : yes
}

/** A click outside the card: cancel, or OK when there is no cancel button (an alert) */
export const outsideAnswer = (spec: UiDialogSpec) => !spec.cancel

/** A key pressed while the dialog is open: 'yes' (Enter), 'no' (Esc), or 'block' (every other key: it must not reach
 *  what is below) */
export const dialogKeyAction = (key: string): 'yes' | 'no' | 'block' => key === 'Escape' ? 'no' : key === 'Enter' ? 'yes' : 'block'

/** How long the thumbs-up stays (ms) and how long the sync notice stays before fading (ms) */
export const SUCCESS_MS = 1600
export const TOAST_MS = 3500
/** The cursor goes into the field (or onto OK) this long after the dialog opens (ms) */
export const DIALOG_FOCUS_MS = 30
